/* istanbul ignore file */

import { useCallback, useMemo } from 'react';
import type { LocationObject } from 'expo-location';
import { defineMessages, useIntl } from 'react-intl';

import { useMachine } from '@xstate/react';
import { useClient } from 'urql';

import { useNoticeBannersStackContext, useSafeRouteParams } from '@sg/garnish';

import { useFeatureFlag } from '@order/LaunchDarkly';
import { getEnvVars } from '@order/utils';

import { useIsLoggedIn } from '../../../../context/AuthMachine';
import { useLastInteractedStore } from '../../../../context/LastInteractedStore';
import { createLocationSearchMachine } from '../../machine';
import { useLocationSearchNavigation } from '../useLocationSearchNavigation';
import { useLocationSearchTelemetry } from '../useLocationSearchTelemetry';

import type {
  BaseLocationRadiusConfig,
  LocationSearchMachineContext,
  LocationSearchMachineEvents,
  LocationsSearchType,
  PositionCoordinates,
} from '../../machine';

// ─────────────────────────────────────────────────────────────────────────────

export const useInitLocationSearchMachine = (props: UseInitLocationSearchMachine) => {
  const { minimalCharactersForSearch, navigateToLocation, navigateToDeliveryLocation } = props;

  const client = useClient();
  const routeParams = useSafeRouteParams<{ orderChannel?: string }>();

  const lastInteractedStore = useLastInteractedStore();
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { formatMessage } = useIntl();
  const locationSearchNavigation = useLocationSearchNavigation();
  const {
    trackUserGeolocation,
    trackLocationSearchType,
    trackLocationSearchByArea,
    trackLocationSearchByDeliveryPlaceID,
    trackFocusedLocationPinId,
    trackNavigateToLocationMenu,
    trackNavigateToDeliveryLocationMenu,
    trackSearchPredictionsAndRestaurantsError,
    trackSearchForPredictionsAndRestaurants,
    trackSearchByDeliveryPlaceIdError,
  } = useLocationSearchTelemetry();
  const isSignedIn = useIsLoggedIn();

  // ─────────────────────────────────────────────────────────────────────

  const initialOrderChannel = getInitialLocationSearchType(routeParams?.orderChannel); // prettier-ignore

  // ─── Flags ───────────────────────────────────────────────────────────

  const isDeliveryPriceDifferentiationEnabled = useFeatureFlag(
    'CELS-2439-enable-delivery-price-differentiation-disclosure',
  );
  const isOutpostPriceDifferentiationEnabled = useFeatureFlag(
    'CELS-2489-enable-outpost-price-differentiation-disclosure',
  );
  const isSearchByAddressIdEnabled = useFeatureFlag(
    'CELS-4617-order-search-delivery-locations-by-address-id-enabled',
  );
  const isRadarPickupAutocompleteEnabled = useFeatureFlag('cels-4731-radar-pickup-autocomplete');
  const isInitialOrderChannelEnabled = useFeatureFlag('CELS-4848-initial-order-channel-enabled');
  const baseLocationRadiusConfig = useFeatureFlag('CELS-4859-base-location-radius-config');
  const isRadarDeliveryEnabled = useFeatureFlag(
    'cels-4823-enable-radar-delivery-address-predictions',
  );

  // ─────────────────────────────────────────────────────────────────────

  const lastInteractedStoreAddressId = lastInteractedStore?.addressId;

  // ─── Actions ─────────────────────────────────────────────────────────

  const onUserLocationChanged = useCallback(
    (_: LocationSearchMachineContext, event: UserLocationEvent) => {
      trackUserGeolocation(event.data);
    },
    [trackUserGeolocation],
  );

  const onCurrentPositionChanged = useCallback(
    (
      _: LocationSearchMachineContext,
      event: {
        data: { currentPositionCoordinates: PositionCoordinates | undefined };
      },
    ) => {
      if (!event.data?.currentPositionCoordinates) return;

      trackUserGeolocation({ coords: event.data.currentPositionCoordinates });
    },
    [trackUserGeolocation],
  );

  const onLocationTypeChangeEvent = useCallback(
    (context: LocationSearchMachineContext) => {
      trackLocationSearchType(context);

      if (isInitialOrderChannelEnabled) {
        locationSearchNavigation.setOrderChannelParams(context.locationSearchType); // prettier-ignore
      }
    },
    [isInitialOrderChannelEnabled, locationSearchNavigation, trackLocationSearchType],
  );

  const onNavigateToLocationEvent = useCallback(
    (context: LocationSearchMachineContext, event: NavigateToLocationEvent) => {
      trackNavigateToLocationMenu(event?.entryPoint)(context);
    },
    [trackNavigateToLocationMenu],
  );

  const onNavigateToDeliveryLocationEvent = useCallback(
    (context: LocationSearchMachineContext, event: NavigateToDeliveryLocationEvent) => {
      trackNavigateToDeliveryLocationMenu(event?.entryPoint)(context);
    },
    [trackNavigateToDeliveryLocationMenu],
  );

  const onLocationWarningConfirmEvent = useMemo(
    () => trackNavigateToLocationMenu('location-warning'),
    [trackNavigateToLocationMenu],
  );

  const onSearchPredictionsAndRestaurantsError = useCallback(() => {
    trackSearchPredictionsAndRestaurantsError();

    addNoticeBanner(
      {
        id: 'location-search.notice_banner.predictions-and-restaurants-error',
        text: formatMessage(messages.predictionsAndRestaurantsError),
        palette: 'caution',
      },
      true,
    );
  }, [addNoticeBanner, formatMessage, trackSearchPredictionsAndRestaurantsError]);

  const onSearchByDeliveryPlaceIdError = useCallback(() => {
    trackSearchByDeliveryPlaceIdError();

    addNoticeBanner(
      {
        id: 'location-search.notice_banner.search-by-delivery-place-id-error',
        text: formatMessage(messages.searchByDeliveryPlaceIdError),
        palette: 'caution',
      },
      true,
    );
  }, [addNoticeBanner, formatMessage, trackSearchByDeliveryPlaceIdError]);

  const onSearchByCurrentPositionError = useCallback(() => {
    addNoticeBanner(
      {
        id: 'location-search.notice_banner.search-by-current-position-error',
        text: formatMessage(messages.searchByCurrentPositionError),
        palette: 'neutral',
      },
      true,
    );
  }, [addNoticeBanner, formatMessage]);

  // ─── Guards ──────────────────────────────────────────────────────────

  const checkIfSignedIn = useCallback(() => isSignedIn, [isSignedIn]);

  const checkIfShouldShowDeliveryAddressForm = useCallback(
    (context: LocationSearchMachineContext) => !context.deliveryLocation?.addressId,
    [],
  );

  const checkIfInitialOrderChannelEnabledAndSignedIn = useCallback(
    () => isInitialOrderChannelEnabled && isSignedIn,
    [isInitialOrderChannelEnabled, isSignedIn],
  );

  const checkIfInitialOrderChannelEnabled = useCallback(
    () => isInitialOrderChannelEnabled,
    [isInitialOrderChannelEnabled],
  );

  const checkIfPickupOrderChannel = useCallback(
    (context: LocationSearchMachineContext) =>
      isInitialOrderChannelEnabled && context.locationSearchType === 'pickup',
    [isInitialOrderChannelEnabled],
  );
  const checkIfDeliveryOrderChannel = useCallback(
    (context: LocationSearchMachineContext) =>
      isInitialOrderChannelEnabled && context.locationSearchType === 'delivery',
    [isInitialOrderChannelEnabled],
  );
  const checkIfOutpostOrderChannel = useCallback(
    (context: LocationSearchMachineContext) =>
      isInitialOrderChannelEnabled && context.locationSearchType === 'outpost',
    [isInitialOrderChannelEnabled],
  );

  // ─── Initialize Machine ──────────────────────────────────────────────

  const locationSearchMachine = useMemo(() => {
    return createLocationSearchMachine({
      client,
      lastInteractedStoreAddressId,
      minimalCharactersForSearch,
      shouldUseCurrentPosition: true,
      googlePlacesApiKey: GOOGLE_API_KEY,
      shouldUseDeliveryDisclosureFields: isDeliveryPriceDifferentiationEnabled,
      shouldUseOutpostDisclosureFields: isOutpostPriceDifferentiationEnabled,
      shouldUseRadarForPickup: isRadarPickupAutocompleteEnabled,
      shouldUseRadarForDelivery: isRadarDeliveryEnabled,
    });
  }, [
    client,
    isDeliveryPriceDifferentiationEnabled,
    isOutpostPriceDifferentiationEnabled,
    isRadarPickupAutocompleteEnabled,
    isRadarDeliveryEnabled,
    lastInteractedStoreAddressId,
    minimalCharactersForSearch,
  ]);

  return useMachine(locationSearchMachine, {
    context: {
      shouldUseImprovedDeliverySearch: isSearchByAddressIdEnabled,
      locationSearchType: isInitialOrderChannelEnabled ? initialOrderChannel : undefined, // prettier-ignore
      baseLocationRadiusConfig: getBaseLocationRadiusConfig(baseLocationRadiusConfig), // prettier-ignore
    },
    actions: {
      navigateToLocation: navigateToLocation ?? locationSearchNavigation.navigateToLocation,
      navigateToDeliveryLocation:
        navigateToDeliveryLocation ?? locationSearchNavigation.navigateToDeliveryLocation,

      // ─── Event Listeners ─────────────────────────────────────────────────

      // @ts-expect-error TS(2322): action type mismatch with xstate machine actions
      onUserLocationChanged,
      onLocationTypeChangeEvent,
      onNavigateToLocationEvent,
      onNavigateToDeliveryLocationEvent,
      onLocationWarningConfirmEvent,
      onSearchByAreaEvent: trackLocationSearchByArea,
      onSearchByDeliveryPlaceIdEvent: trackLocationSearchByDeliveryPlaceID,
      onFocusedPinIdChangeEvent: trackFocusedLocationPinId,
      onCurrentPositionChanged,
      onSearchForPredictions: trackSearchForPredictionsAndRestaurants,
      onSearchPredictionsAndRestaurantsError,
      onSearchByDeliveryPlaceIdError,
      onSearchByCurrentPositionError,
    },
    guards: {
      checkIfSignedIn,
      checkIfShouldShowDeliveryAddressForm,
      checkIfInitialOrderChannelEnabledAndSignedIn,
      checkIfInitialOrderChannelEnabled,
      checkIfPickupOrderChannel,
      checkIfDeliveryOrderChannel,
      checkIfOutpostOrderChannel,
    },
  });
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  predictionsAndRestaurantsError: {
    defaultMessage:
      'Something went wrong while searching for locations, try again in a few moments...',
    description: 'Location Search | Predictions and Restaurants Error',
  },
  searchByDeliveryPlaceIdError: {
    defaultMessage:
      'Something went wrong while searching for the delivery location. Please try again in a few moments or use a different address.',
    description: 'Location Search | Search by Delivery Place ID Error',
  },
  searchByCurrentPositionError: {
    defaultMessage:
      'Something went wrong while getting your current position. Please check your location permissions and availability, and try again.',
    description: 'Location Search | Get Current Position Error',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const { GOOGLE_API_KEY } = getEnvVars();

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getInitialLocationSearchType(
  initialOrderChannel: string | undefined,
): LocationsSearchType | undefined {
  if (initialOrderChannel === 'delivery') return 'delivery';
  if (initialOrderChannel === 'outpost') return 'outpost';
  if (initialOrderChannel === 'pickup') return 'pickup';
}

function getBaseLocationRadiusConfig(
  maybeConfig: Record<string, number>,
): BaseLocationRadiusConfig {
  return {
    SMALLEST: typeof maybeConfig.SMALLEST === 'number' ? maybeConfig.SMALLEST : 5, // prettier-ignore
    SMALL: typeof maybeConfig.SMALL === 'number' ? maybeConfig.SMALL : 15,
    MEDIUM: typeof maybeConfig.MEDIUM === 'number' ? maybeConfig.MEDIUM : 40,
    LARGE: typeof maybeConfig.LARGE === 'number' ? maybeConfig.LARGE : 200,
  };
}

// ─── Types ───────────────────────────────────────────────────────────────────

type UseInitLocationSearchMachine = Readonly<{
  minimalCharactersForSearch: number;

  /*
    Optional actions that initiates the navigation process for the selected location.
    When those properties are not provided, the standard navigation is used.

    Helpful when a custom navigation method is utilized or when other actions
    need to be performed before navigation.
   */

  navigateToLocation?: (context: LocationSearchMachineContext) => void;
  navigateToDeliveryLocation?: (context: LocationSearchMachineContext) => void;
}>;

type NavigateToLocationEvent = Extract<
  LocationSearchMachineEvents,
  Readonly<{ type: 'NAVIGATE_TO_LOCATION' }>
>;

type NavigateToDeliveryLocationEvent = Extract<
  LocationSearchMachineEvents,
  Readonly<{ type: 'NAVIGATE_TO_DELIVERY_LOCATION' }>
>;

type UserLocationEvent = Readonly<{
  data: LocationObject;
}>;
